import { ChannelIcon, ErrorState, Select, TraceChip } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesSquare, Radio, Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { CustomerPanel } from '../inbox/CustomerPanel';
import { InboxOverview } from '../inbox/Overview';
import { ConversationList, type ListFilters } from '../inbox/List';
import { Thread } from '../inbox/Thread';
import type { ConversationDetail, PresenceEntry } from '../inbox/types';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { type ConnectionState, type RealtimeFrame } from '../lib/realtime';
import { useRealtimeChannel } from '../lib/realtime-context';

interface MessageReceived {
  messageId: string;
  conversationId: string;
  personDisplayName: string;
  channel: string;
  preview: string;
  conversationCreated: boolean;
}

const SIM_CHANNELS = [
  'whatsapp',
  'instagram',
  'facebook',
  'tiktok',
  'snapchat',
  'email',
  'sms',
  'webchat',
  'voice',
] as const;

function ConnectionPill({ state }: { state: ConnectionState }) {
  const { t } = useTranslation();
  return (
    <span className="pill" data-testid="rt-state" data-state={state}>
      <span
        className={`pill-dot${state === 'live' ? ' live' : ''}`}
        style={state === 'live' ? undefined : { background: 'var(--tx-faint)' }}
      />
      {t(`inbox.connection.${state}`)}
    </span>
  );
}

function SimulateForm() {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<(typeof SIM_CHANNELS)[number]>('whatsapp');
  const [name, setName] = useState(() => t('inbox.simulate.defaultName'));
  const [phone, setPhone] = useState('+971 50 222 3344');
  const [text, setText] = useState(() => t('inbox.simulate.defaultText'));
  const [result, setResult] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async () => {
      const started = performance.now();
      await api('/v1/dev/simulate-inbound', {
        method: 'POST',
        body: JSON.stringify({ as: channel, from: { type: 'phone', value: phone, name }, text }),
      });
      return Math.round(performance.now() - started);
    },
    onSuccess: (ms) => setResult(t('inbox.simulate.accepted', { ms })),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    send.mutate();
  };
  return (
    <form className="card" onSubmit={submit} data-testid="simulate-form">
      <div className="card-head">
        <h2>{t('inbox.simulate.title')}</h2>
      </div>
      <div className="card-pad">
        <p className="mute" style={{ marginTop: 0, fontSize: 'var(--t-xs)' }}>
          {t('inbox.simulate.hint')}
        </p>
        <div className="field">
          <label htmlFor="sim-channel">{t('inbox.simulate.channel')}</label>
          <Select
            id="sim-channel"
            value={channel}
            onChange={(v) => setChannel(v as typeof channel)}
            options={SIM_CHANNELS.map((c) => ({
              value: c,
              label: t(`channels.${c}`),
              icon: <ChannelIcon channel={c} size={11} />,
            }))}
          />
        </div>
        <div className="field">
          <label htmlFor="sim-name">{t('inbox.simulate.name')}</label>
          <input id="sim-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="sim-phone">{t('inbox.simulate.phone')}</label>
          <input
            id="sim-phone"
            className="input mono"
            dir="ltr"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="sim-text">{t('inbox.simulate.text')}</label>
          <textarea
            id="sim-text"
            className="textarea"
            dir="auto"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <button className="btn btn-block" type="submit" disabled={send.isPending || !text.trim()}>
          <Send size={15} className="icon-dir" />
          {send.isPending ? t('inbox.simulate.sending') : t('inbox.simulate.submit')}
        </button>
        {send.isError && <ErrorState error={send.error} />}
        {result && (
          <p className="faint" role="status" style={{ fontSize: 'var(--t-xs)', marginBottom: 0 }}>
            {result}
          </p>
        )}
      </div>
    </form>
  );
}

export function InboxPage() {
  const { t } = useTranslation();
  const { session, can } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const [filters, setFilters] = useState<ListFilters>({
    status: 'open',
    assignee: 'any',
    channel: '',
    team: '',
    q: '',
    sla: '',
    kind: '',
  });
  const [feed, setFeed] = useState<RealtimeFrame<MessageReceived>[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  const [presence, setPresence] = useState<Record<string, PresenceEntry[]>>({});

  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const sendRef = useRef<(f: unknown) => void>(() => undefined);
  const lastAnnounce = useRef(0);
  const announce = useCallback(
    (state: 'viewing' | 'typing' | 'left', conversationId = selectedRef.current) => {
      if (!conversationId) return;
      if (state === 'viewing') lastAnnounce.current = Date.now();
      sendRef.current({ type: 'presence', conversationId, state });
    },
    [],
  );

  const onFrame = useCallback(
    (f: RealtimeFrame) => {
      if (f.type === 'presence') {
        const p = f.payload as PresenceEntry & { conversationId: string };
        if (p.userId === session?.user.id) return;
        setPresence((prev) => {
          const list = (prev[p.conversationId] ?? []).filter((x) => x.userId !== p.userId);
          return { ...prev, [p.conversationId]: p.state === 'left' ? list : [...list, p] };
        });
        // Someone just joined the conversation I'm on: answer so they see me without waiting for a heartbeat.
        if (
          p.state === 'viewing' &&
          p.conversationId === selectedRef.current &&
          Date.now() - lastAnnounce.current > 3_000
        )
          announce('viewing');
        return;
      }
      const payload = f.payload as { conversationId?: string };
      if (f.type === 'message.received') {
        const frame = f as RealtimeFrame<MessageReceived>;
        // Delivery is at-least-once: drop frames already shown.
        setFeed((prev) =>
          prev.some((x) => x.eventId === frame.eventId) ? prev : [frame, ...prev].slice(0, 30),
        );
        setFlash(frame.payload.conversationId);
      }
      if (['message.received', 'message.status_changed', 'conversation.updated'].includes(f.type)) {
        void qc.invalidateQueries({ queryKey: ['conversations'] });
        if (payload.conversationId) {
          void qc.invalidateQueries({ queryKey: ['messages', payload.conversationId] });
          void qc.invalidateQueries({ queryKey: ['conversation', payload.conversationId] });
        }
      }
    },
    [qc, session?.user.id, announce],
  );
  const rt = useRealtimeChannel(onFrame);
  sendRef.current = rt.send;
  // Announce as soon as the connection is live (an announcement made while connecting would be lost).
  useEffect(() => {
    if (rt.state === 'live' && selected) announce('viewing');
  }, [rt.state, selected, announce]);
  const sendPresence = announce;
  const select = (id: string | null) => setParams(id ? { c: id } : {}, { replace: !id });
  const devTools = import.meta.env.DEV && can('dev:simulate');
  // The selected conversation's customer, for the side panel (shares the thread's cached query).
  const selectedDetail = useQuery({
    queryKey: ['conversation', selected],
    queryFn: () => api<ConversationDetail>(`/v1/conversations/${selected}`),
    enabled: !!selected,
  });
  const selectedPerson = selected ? (selectedDetail.data?.person.id ?? null) : null;
  const [sideOpen, setSideOpen] = useState(false);

  return (
    <>
      <div className="inbox-head">
        <h1>{t('inbox.title')}</h1>
        <ConnectionPill state={rt.state} />
      </div>
      <div
        className={`inbox-grid${devTools || selectedPerson ? '' : ' no-side'}`}
        data-view={selected ? 'thread' : 'list'}
        data-side={sideOpen && selectedPerson ? 'open' : 'closed'}
      >
        <section className="card conv-pane" aria-label={t('inbox.conversations')}>
          <ConversationList
            filters={filters}
            setFilters={setFilters}
            active={selected}
            onSelect={select}
            flash={flash}
          />
        </section>
        <section className="card thread" aria-label={t('inbox.title')}>
          {selected ? (
            <Thread
              key={selected}
              id={selected}
              onBack={() => select(null)}
              presence={presence[selected] ?? []}
              sendPresence={sendPresence}
              onToggleCustomer={() => setSideOpen((o) => !o)}
            />
          ) : (
            <InboxOverview onFilter={setFilters} />
          )}
        </section>
        {(devTools || selectedPerson) && (
          <div className="side-stack">
            {selected && selectedPerson && (
              <CustomerPanel personId={selectedPerson} conversationId={selected} />
            )}
            {devTools && (
              <details className="dev-tools" open={!selected}>
                <summary>{t('inbox.devTools')}</summary>
                <section className="panel-dark" aria-label={t('inbox.liveFeed')}>
                  <div className="card-head">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Radio size={16} />
                      {t('inbox.liveFeed')}
                    </h2>
                  </div>
                  {feed.length === 0 ? (
                    <div className="empty faint">
                      <MessagesSquare size={20} />
                      {t('inbox.liveEmpty')}
                    </div>
                  ) : (
                    <ul className="feed" data-testid="live-feed">
                      {feed.map((f) => (
                        <li key={f.eventId} data-trace-id={f.traceId}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 8,
                              marginBottom: 4,
                            }}
                          >
                            <strong dir="auto">{f.payload.personDisplayName}</strong>
                            <TraceChip traceId={f.traceId} />
                          </div>
                          <div
                            className="mute"
                            dir="auto"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {f.payload.preview}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                <SimulateForm />
              </details>
            )}
          </div>
        )}
      </div>
    </>
  );
}
